/**
 * RNNoise (WASM) loading — the denoiser that runs in the PUBLISH path.
 *
 * The whole point of doing this in the browser is that the server never has to
 * decode audio: a denoised Opus stream is forwarded byte-for-byte like any
 * other, so 300 concurrent participants still cost the box nothing but fan-out.
 *
 * Everything here is best-effort. If the WASM will not load — old browser, a
 * blocked asset, an AudioWorklet-less WebView — the caller falls back to the
 * native `noiseSuppression` constraint and the user is told which one is live.
 */
import { RnnoiseWorkletNode, loadRnnoise } from '@sapphi-red/web-noise-suppressor';
import rnnoiseWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise.wasm?url';
import rnnoiseSimdWasmUrl from '@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url';
import rnnoiseWorkletUrl from '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url';

/** RNNoise is trained at 48 kHz. Run the graph anywhere else and it hears mush. */
export const RNNOISE_SAMPLE_RATE = 48_000;

/** Mono in, mono out — matches `AUDIO_SETTINGS.channelCount`. */
const MAX_CHANNELS = 1;

export function rnnoiseSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.AudioContext === 'function' &&
    typeof AudioWorkletNode === 'function' &&
    typeof WebAssembly === 'object'
  );
}

/** The binary is fetched once per tab and re-used for every AudioContext. */
let binaryPromise: Promise<ArrayBuffer> | null = null;

/**
 * `addModule` is per-worklet-global, so it must be repeated for every context.
 * A WeakSet keyed on the worklet lets a closed context be collected.
 */
const registered = new WeakSet<AudioWorklet>();

/**
 * Builds an RNNoise worklet node on `context`, or returns null when the
 * denoiser is unavailable. Never throws — an unusable denoiser is a downgrade,
 * not an error the meeting should see.
 */
export async function createRnnoiseNode(
  context: AudioContext,
): Promise<RnnoiseWorkletNode | null> {
  if (!rnnoiseSupported()) return null;
  try {
    binaryPromise ??= loadRnnoise({ url: rnnoiseWasmUrl, simdUrl: rnnoiseSimdWasmUrl });
    const wasmBinary = await binaryPromise;

    if (!registered.has(context.audioWorklet)) {
      await context.audioWorklet.addModule(rnnoiseWorkletUrl);
      registered.add(context.audioWorklet);
    }
    return new RnnoiseWorkletNode(context, { maxChannels: MAX_CHANNELS, wasmBinary });
  } catch {
    // A transient fetch failure must stay retryable — the next toggle re-fetches.
    binaryPromise = null;
    return null;
  }
}
